import React, { useState, useEffect } from 'react';
import PrimaryButton from '../../Common/PrimaryButton';

/**
 * Formulário para criar ou editar alergia
 */
const AlergiaForm = ({ onClose, onSubmit, initialData = {} }) => {
    const isEditing = !!initialData.id;
    const [formData, setFormData] = useState({
        tipo: 'medicamento',
        descricao: '',
        severidade: '',
        data_descoberta: '',
        reacao: '',
        tratamento: '',
        observacoes: ''
    });
    const [formError, setFormError] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);

    useEffect(() => {
        if (isEditing && initialData) {
            setFormData({
                tipo: initialData.tipo || 'medicamento',
                descricao: initialData.descricao || '',
                severidade: initialData.severidade || '',
                data_descoberta: initialData.data_descoberta 
                    ? new Date(initialData.data_descoberta).toISOString().substring(0, 10) 
                    : '',
                reacao: initialData.reacao || '',
                tratamento: initialData.tratamento || '',
                observacoes: initialData.observacoes || ''
            });
        }
        setFormError(null);
    }, [initialData, isEditing]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setFormError(null);

        if (!formData.descricao.trim()) {
            setFormError('A descrição é obrigatória.');
            return;
        }

        setIsSubmitting(true);
        try {
            await onSubmit(formData);
            onClose();
        } catch (error) {
            setFormError(error?.response?.data?.message || error?.message || 'Erro ao salvar alergia.');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <form onSubmit={handleSubmit} className="space-y-4">
            {formError && (
                <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded text-sm" role="alert">
                    {formError}
                </div>
            )}

            <div>
                <label htmlFor="tipo" className="block text-sm font-medium text-gray-700 mb-1">
                    Tipo <span className="text-red-500">*</span>
                </label>
                <select
                    id="tipo"
                    name="tipo"
                    value={formData.tipo}
                    onChange={handleChange}
                    className="w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-emerald-500 focus:border-emerald-500"
                    required
                >
                    <option value="medicamento">Medicamento</option>
                    <option value="alimento">Alimento</option>
                    <option value="outro">Outro</option>
                </select>
            </div>

            <div>
                <label htmlFor="descricao" className="block text-sm font-medium text-gray-700 mb-1">
                    Descrição <span className="text-red-500">*</span>
                </label>
                <input
                    type="text"
                    id="descricao"
                    name="descricao"
                    value={formData.descricao}
                    onChange={handleChange}
                    className="w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-emerald-500 focus:border-emerald-500"
                    placeholder="Ex: Penicilina, Amendoim, Látex..."
                    required
                />
            </div>

            <div>
                <label htmlFor="severidade" className="block text-sm font-medium text-gray-700 mb-1">
                    Severidade
                </label>
                <select
                    id="severidade"
                    name="severidade"
                    value={formData.severidade}
                    onChange={handleChange}
                    className="w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-emerald-500 focus:border-emerald-500"
                >
                    <option value="">Selecione...</option>
                    <option value="leve">Leve</option>
                    <option value="moderada">Moderada</option>
                    <option value="grave">Grave</option>
                </select>
            </div>

            <div>
                <label htmlFor="data_descoberta" className="block text-sm font-medium text-gray-700 mb-1">
                    Data de Descoberta
                </label>
                <input
                    type="date"
                    id="data_descoberta"
                    name="data_descoberta"
                    value={formData.data_descoberta}
                    onChange={handleChange}
                    max={new Date().toISOString().substring(0, 10)}
                    className="w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-emerald-500 focus:border-emerald-500"
                />
            </div>

            <div>
                <label htmlFor="reacao" className="block text-sm font-medium text-gray-700 mb-1">
                    Reação Alérgica
                </label>
                <textarea
                    id="reacao"
                    name="reacao"
                    value={formData.reacao}
                    onChange={handleChange}
                    rows={3}
                    className="w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-emerald-500 focus:border-emerald-500"
                    placeholder="Descreva a reação alérgica quando exposto ao alérgeno..."
                />
            </div>

            <div>
                <label htmlFor="tratamento" className="block text-sm font-medium text-gray-700 mb-1">
                    Tratamento em Caso de Exposição
                </label>
                <textarea
                    id="tratamento"
                    name="tratamento"
                    value={formData.tratamento}
                    onChange={handleChange}
                    rows={3}
                    className="w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-emerald-500 focus:border-emerald-500"
                    placeholder="Descreva o tratamento a ser aplicado em caso de exposição..."
                />
            </div>

            <div>
                <label htmlFor="observacoes" className="block text-sm font-medium text-gray-700 mb-1">
                    Observações
                </label>
                <textarea
                    id="observacoes"
                    name="observacoes"
                    value={formData.observacoes}
                    onChange={handleChange}
                    rows={2}
                    className="w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-emerald-500 focus:border-emerald-500"
                    placeholder="Observações adicionais..."
                />
            </div>

            <div className="flex justify-end gap-3 pt-4 border-t">
                <button
                    type="button"
                    onClick={onClose}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-emerald-500"
                    disabled={isSubmitting}
                >
                    Cancelar
                </button>
                <PrimaryButton type="submit" disabled={isSubmitting}>
                    {isSubmitting ? 'Salvando...' : (isEditing ? 'Atualizar' : 'Salvar')}
                </PrimaryButton>
            </div>
        </form>
    );
};

export default AlergiaForm;

